import { clamp } from '@blocksuite/global/gfx';
import { WithDisposable } from '@blocksuite/global/lit';
import { isSameDay, isSameMonth, isToday } from 'date-fns';
import {
  html,
  LitElement,
  nothing,
  type PropertyValues,
  type TemplateResult,
} from 'lit';
import { property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';

import { arrowLeftIcon } from './icons.js';
import { datePickerStyle } from './style.js';
import { getMonthMatrix, toDate } from './utils.js';

const days = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const months = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

export interface DateCell {
  date: Date;
  label: string;
  isToday: boolean;
  notCurrentMonth: boolean;
  selected?: boolean;
  inRange?: boolean;
  rangeStart?: boolean;
  rangeEnd?: boolean;
  tabIndex?: number;
}

type NavActionArg = {
  action: () => void;
  disable?: boolean;
};

/**
 * Date picker
 */
export class DatePicker extends WithDisposable(LitElement) {
  static override styles = datePickerStyle;

  /** current active month */
  private _cursor = new Date();

  private readonly _maxYear = 2099;

  private readonly _minYear = 1000;

  get _cardStyle() {
    return {
      '--cell-size': `${this.size}px`,
      '--gap-h': `${this.gapH}px`,
      '--gap-v': `${this.gapV}px`,
      'min-width': `${this.cardWidth}px`,
      'min-height': `${this.cardHeight}px`,
      padding: `${this.padding}px`,
    };
  }

  get cardHeight() {
    const rowNum = 7;
    return this.size * rowNum + this.padding * 2 + this.gapV * (rowNum - 1) - 2;
  }

  get cardWidth() {
    const colNum = 7;
    return this.size * colNum + this.padding * 2 + this.gapH * (colNum - 1);
  }

  get date() {
    return this._cursor.getDate();
  }

  get day() {
    return this._cursor.getDay();
  }

  get dayLabel() {
    return days[this.day];
  }

  get minHeight() {
    const rowNum = this._matrix.length;
    return this.size * rowNum + this.padding * 2 + this.gapV * (rowNum - 1) - 2;
  }

  get month() {
    return this._cursor.getMonth();
  }

  get monthLabel() {
    return months[this.month];
  }

  get year() {
    return this._cursor.getFullYear();
  }

  get yearLabel() {
    return this.year;
  }

  /** Cell */
  private _cellRenderer(cell: DateCell) {
    const classes = classMap({
      interactive: true,
      'date-cell': true,
      'date-cell--today': cell.isToday,
      'date-cell--not-curr-month': cell.notCurrentMonth,
      'date-cell--selected': !!cell.selected,
      'date-cell--in-range': !!cell.inRange,
      'date-cell--range-start': !!cell.rangeStart,
      'date-cell--range-end': !!cell.rangeEnd,
    });
    const dateRaw = `${cell.date.getFullYear()}-${cell.date.getMonth()}-${cell.date.getDate()}(${cell.date.getDay()})`;
    return html`<button
      tabindex=${cell.tabIndex ?? -1}
      aria-label=${dateRaw}
      data-date=${dateRaw}
      class=${classes}
      @click=${() => {
        this._onChange(cell.date);
      }}
      @mouseenter=${() => this._onCellHover(cell.date)}
    >
      ${cell.label}
    </button>`;
  }

  private _dateContent() {
    return html` <div class="date-picker-header">
        <div class="date-picker-header__buttons">
          <button
            class="date-picker-header__date interactive"
            @click=${() => this.toggleMonthSelector()}
          >
            <div>${this.monthLabel}</div>
          </button>

          <button
            class="date-picker-header__date interactive"
            @click=${() => this.toggleYearSelector()}
          >
            <div>${this.yearLabel}</div>
          </button>
        </div>

        ${this._navAction(
          () => this._moveMonth(-1),
          () => this._moveMonth(1),
          html`<button
            tabindex="0"
            aria-label="today"
            class="action-label interactive today"
            @click=${() => {
              this._onChange(new Date());
            }}
          >
            <span>TODAY</span>
          </button>`
        )}
      </div>
      ${this._dayHeaderRenderer()}
      <div
        class="date-picker-weeks"
        @mouseleave=${() => this._onCellHover(undefined)}
      >
        ${this._matrix.map(
          week =>
            html`<div class="date-picker-week">
              ${week.map(cell => this._cellRenderer(cell))}
            </div>`
        )}
      </div>
      ${this._timeInputs()}
      ${this.onClear
        ? html`<div class="date-picker-footer">
            <button
              tabindex="0"
              aria-label="clear"
              class="footer-button interactive"
              @click=${() => this.onClear?.()}
            >
              Clear
            </button>
          </div>`
        : nothing}`;
  }

  /** Week header */
  private _dayHeaderRenderer() {
    return html`<div class="days-header">
      ${days.map(day => html`<div class="date-cell">${day}</div>`)}
    </div>`;
  }

  private _getMatrix() {
    this._matrix = getMonthMatrix(this._cursor).map(row => {
      return row.map(date => {
        const tabIndex = isSameDay(date, this._cursor) ? 0 : -1;
        let selected = false;
        let inRange = false;
        let rangeStart = false;
        let rangeEnd = false;

        if (this.range) {
          // while a selection is pending (start set, end not yet), the hovered
          // date previews the would-be range; once complete, only the committed
          // range is highlighted.
          const previewEnd =
            this._rangeStart && !this._rangeEnd ? this._hoverDate : undefined;
          const refEnd = previewEnd ?? this._rangeEnd;
          if (this._rangeStart && isSameDay(date, this._rangeStart)) {
            selected = true;
            rangeStart = true;
          }
          if (refEnd && isSameDay(date, refEnd)) {
            selected = true;
            rangeEnd = true;
          }
          if (this._rangeStart && refEnd) {
            const lo = Math.min(this._rangeStart.getTime(), refEnd.getTime());
            const hi = Math.max(this._rangeStart.getTime(), refEnd.getTime());
            const t = date.getTime();
            if (t > lo && t < hi) {
              inRange = true;
            }
          }
        } else {
          selected = this.value ? isSameDay(date, toDate(this.value)) : false;
        }

        return {
          date,
          label: date.getDate().toString(),
          isToday: isToday(date),
          notCurrentMonth: !isSameMonth(date, this._cursor),
          selected,
          inRange,
          rangeStart,
          rangeEnd,
          tabIndex,
        } satisfies DateCell;
      });
    });
  }

  private _getYearMatrix() {
    // every decade has 12 years
    const no = Math.floor((this._yearCursor - this._minYear) / 12);
    const decade = no * 12;
    const start = this._minYear + decade;
    const end = start + 12;
    this._yearMatrix = Array.from(
      { length: end - start },
      (_, i) => start + i
    ).filter(v => v >= this._minYear && v <= this._maxYear);
  }

  private _modeDecade(offset: number) {
    this._yearCursor = clamp(
      this._yearCursor + offset,
      this._minYear,
      this._maxYear
    );
    this._getYearMatrix();
  }

  private _timeInputs() {
    if (!this.showTime) return nothing;

    const pad = (n: number) => n.toString().padStart(2, '0');
    const hourOptions = Array.from({ length: 24 }, (_, h) => pad(h));
    const minuteOptions = Array.from({ length: 60 }, (_, m) => pad(m));

    const hoursOf = (date?: Date) => (date ? pad(date.getHours()) : '');
    const minutesOf = (date?: Date) => (date ? pad(date.getMinutes()) : '');

    const select = (
      options: string[],
      value: string,
      disabled: boolean,
      onChange: (v: number) => void
    ) =>
      html`<select
        class="date-picker-time-select"
        aria-label="time"
        ?disabled=${disabled}
        @change=${(e: Event) => {
          e.stopPropagation();
          onChange(Number((e.target as HTMLSelectElement).value));
        }}
      >
        ${options.map(
          o => html`<option value=${o} ?selected=${o === value}>${o}</option>`
        )}
      </select>`;

    const pair = (
      date: Date | undefined,
      kind: 'start' | 'end' | 'single',
      disabled: boolean
    ) =>
      html`<div class="date-picker-time-pair">
        ${select(hourOptions, hoursOf(date), disabled, h =>
          this._setTime(kind, 'hours', h)
        )}
        <span class="date-picker-time-separator">:</span>
        ${select(minuteOptions, minutesOf(date), disabled, m =>
          this._setTime(kind, 'minutes', m)
        )}
      </div>`;

    if (this.range) {
      return html`<div class="date-picker-time">
        ${pair(this._rangeStart, 'start', !this._rangeStart)}
        <span class="date-picker-time-separator range-gap">–</span>
        ${pair(this._rangeEnd, 'end', !this._rangeEnd)}
      </div>`;
    }

    return html`<div class="date-picker-time">
      ${pair(
        this.value ? new Date(this.value) : undefined,
        'single',
        !this.value
      )}
    </div>`;
  }

  /**
   * Apply an hour/minute picked from the dropdowns to the start/end date of
   * the current selection (or the single date in non-range mode) and notify
   * the host. Selecting a time always implies `hasTime: true`.
   */
  private _setTime(
    kind: 'start' | 'end' | 'single',
    unit: 'hours' | 'minutes',
    value: number
  ) {
    if (kind === 'single') {
      if (!this.value) return;
      const d = this._withUnit(new Date(this.value), unit, value);
      this.value = d.getTime();
      this.onChange?.(d);
      return;
    }

    if (kind === 'start') {
      if (!this._rangeStart) {
        // no date clicked yet — seed from the host-provided value (today or
        // the existing start) so a time can be set before picking dates; the
        // seed must not count as a picked endpoint (see `_onChange`)
        this._rangeStart = this._withUnit(
          toDate(this.value ?? Date.now()),
          unit,
          value
        );
        this._startSeeded = true;
      } else {
        this._rangeStart = this._withUnit(this._rangeStart, unit, value);
      }
    } else {
      if (!this._rangeEnd) return; // end dropdowns are disabled until picked
      this._rangeEnd = this._withUnit(this._rangeEnd, unit, value);
    }

    this.onChangeRange?.({
      start: this._rangeStart!,
      end: this._rangeEnd,
      hasTime: true,
    });
  }

  private _withUnit(date: Date, unit: 'hours' | 'minutes', value: number) {
    const d = new Date(date.getTime());
    if (unit === 'hours') d.setHours(value, d.getMinutes());
    else d.setHours(d.getHours(), value);
    return d;
  }

  private _copyTimeOfDay(from: Date, to: Date) {
    to.setHours(from.getHours(), from.getMinutes());
  }

  /** true while `_rangeStart` only exists because a time was picked first */
  private _startSeeded = false;

  /** end of the previous complete selection, reused when re-picking dates */
  private _stashedEndTime: Date | undefined = undefined;

  private _monthContent() {
    return html` <div class="date-picker-header">
        <button
          class="date-picker-header__date interactive"
          @click=${() => this.toggleMonthSelector()}
        >
          <div>${this._monthPickYearCursor}</div>
        </button>

        ${this._navAction(
          {
            action: () => this._monthPickYearCursor--,
            disable: this._monthPickYearCursor <= this._minYear,
          },
          {
            action: () => this._monthPickYearCursor++,
            disable: this._monthPickYearCursor >= this._maxYear,
          }
        )}
      </div>
      <div class="date-picker-month">
        ${months.map((month, index) => {
          const isActive = this.value
            ? isSameMonth(
                this.value,
                new Date(this._monthPickYearCursor, index, 1)
              )
            : false;
          const classes = classMap({
            'month-cell': true,
            interactive: true,
            active: isActive,
          });
          return html`<button
            tabindex=${this._monthCursor === index ? 0 : -1}
            aria-label=${month}
            class=${classes}
            @click=${() => {
              this._cursor.setMonth(index);
              this._cursor.setFullYear(this._monthPickYearCursor);
              this._mode = 'date';
              this._getMatrix();
            }}
          >
            ${month}
          </button>`;
        })}
      </div>`;
  }

  private _clampCursorYear() {
    const year = this._cursor.getFullYear();
    if (year < this._minYear) {
      this._cursor = new Date(this._minYear, 0, 1);
    } else if (year > this._maxYear) {
      this._cursor = new Date(this._maxYear, 11, 31);
    }
  }

  private _moveMonth(offset: number) {
    this._cursor.setMonth(this._cursor.getMonth() + offset);
    this._clampCursorYear();
    this._getMatrix();
  }

  /** Actions */
  private _navAction(
    prev: NavActionArg | NavActionArg['action'],
    curr: NavActionArg | NavActionArg['action'],
    slot?: TemplateResult
  ) {
    const onPrev = typeof prev === 'function' ? prev : prev.action;
    const onNext = typeof curr === 'function' ? curr : curr.action;
    const prevDisable = typeof prev === 'function' ? false : prev.disable;
    const nextDisable = typeof curr === 'function' ? false : curr.disable;
    const classes = classMap({
      'date-picker-header__action': true,
      'with-slot': !!slot,
    });
    return html`<div class=${classes}>
      <button
        aria-label="previous month"
        class="date-picker-small-action interactive left"
        @click=${onPrev}
        ?disabled=${prevDisable}
      >
        ${arrowLeftIcon}
      </button>
      ${slot ?? nothing}
      <button
        aria-label="next month"
        class="date-picker-small-action interactive right"
        @click=${onNext}
        ?disabled=${nextDisable}
      >
        ${arrowLeftIcon}
      </button>
    </div>`;
  }

  private _onChange(date: Date, emit = true) {
    if (this.range) {
      // two-click range selection: the first click always starts a fresh
      // selection (replacing any completed one), the second click completes
      // it. Click order does not matter — endpoints are normalized so that
      // start = min(A, B) and end = max(A, B).
      //
      // a start that was only seeded by a time pick (`_startSeeded`) does not
      // count as a picked endpoint, so the first date click still begins a
      // fresh selection — while keeping the already chosen time-of-day.
      const pickedStart = this._startSeeded ? undefined : this._rangeStart;
      let newStart: Date;
      if (!pickedStart || this._rangeEnd) {
        const prevStart = this._rangeStart;
        const prevEnd = this._rangeEnd;
        newStart = date;
        if (prevStart) this._copyTimeOfDay(prevStart, date);
        this._stashedEndTime = prevEnd;
        this._rangeEnd = undefined;
      } else {
        const [start, end] =
          date < pickedStart ? [date, pickedStart] : [pickedStart, date];
        newStart = start;
        this._rangeEnd = end;
        if (this._stashedEndTime) {
          this._copyTimeOfDay(this._stashedEndTime, end);
          this._stashedEndTime = undefined;
        }
      }
      this._rangeStart = newStart;
      this._startSeeded = false;
      this._hoverDate = undefined;
      this._cursor = date;
      this._getMatrix();
      if (emit) {
        this.onChangeRange?.({
          start: this._rangeStart,
          end: this._rangeEnd,
          hasTime: this.showTime,
        });
      }
      return;
    }

    this._cursor = date;
    this.value = date.getTime();
    this._getMatrix();
    emit && this.onChange?.(date);
  }

  private _switchMode<T>(map: Record<typeof this._mode, T>) {
    return (map[this._mode] as T) ?? nothing;
  }

  private _yearContent() {
    const startYear = this._yearMatrix[0];
    const endYear = this._yearMatrix[this._yearMatrix.length - 1];
    return html`<div class="date-picker-header">
        <button
          class="date-picker-header__date interactive"
          @click=${() => this.toggleYearSelector()}
        >
          <div>${startYear}-${endYear}</div>
        </button>
        ${this._navAction(
          {
            action: () => this._modeDecade(-12),
            disable: startYear <= this._minYear,
          },
          {
            action: () => this._modeDecade(12),
            disable: endYear >= this._maxYear,
          }
        )}
      </div>
      <div class="date-picker-year">
        ${this._yearMatrix.map(year => {
          const isActive = year === this._cursor.getFullYear();
          const classes = classMap({
            'year-cell': true,
            interactive: true,
            active: isActive,
          });
          return html`<button
            tabindex=${this._yearCursor === year ? 0 : -1}
            aria-label=${year}
            class=${classes}
            @click=${() => {
              this._cursor.setFullYear(year);
              this._mode = 'date';
              this._getMatrix();
            }}
          >
            ${year}
          </button>`;
        })}
      </div>`;
  }

  closeMonthSelector() {
    this._mode = 'date';
  }

  closeYearSelector() {
    this._mode = 'date';
  }

  override connectedCallback(): void {
    super.connectedCallback();
    if (this.value) this._cursor = toDate(this.value);
    this._getMatrix();
  }

  override firstUpdated(): void {
    this._disposables.addFromEvent(
      this,
      'keydown',
      e => {
        e.stopPropagation();
        const directions = new Set([
          'ArrowLeft',
          'ArrowRight',
          'ArrowUp',
          'ArrowDown',
        ]);
        if (directions.has(e.key) && this.isDateCellFocused()) {
          e.preventDefault();

          if (e.key === 'ArrowLeft') {
            this._cursor.setDate(this._cursor.getDate() - 1);
          } else if (e.key === 'ArrowRight') {
            this._cursor.setDate(this._cursor.getDate() + 1);
          } else if (e.key === 'ArrowUp') {
            this._cursor.setDate(this._cursor.getDate() - 7);
          } else if (e.key === 'ArrowDown') {
            this._cursor.setDate(this._cursor.getDate() + 7);
          }
          this._clampCursorYear();
          this._getMatrix();
          setTimeout(this.focusDateCell.bind(this));
        }

        if (directions.has(e.key) && this.isMonthCellFocused()) {
          e.preventDefault();
          if (e.key === 'ArrowLeft') {
            this._monthCursor = (this._monthCursor - 1 + 12) % 12;
          } else if (e.key === 'ArrowRight') {
            this._monthCursor = (this._monthCursor + 1) % 12;
          } else if (e.key === 'ArrowUp') {
            this._monthCursor = (this._monthCursor - 3 + 12) % 12;
          } else if (e.key === 'ArrowDown') {
            this._monthCursor = (this._monthCursor + 3) % 12;
          }
          setTimeout(this.focusMonthCell.bind(this));
        }

        if (directions.has(e.key) && this.isYearCellFocused()) {
          e.preventDefault();
          if (e.key === 'ArrowLeft') {
            this._modeDecade(-1);
          } else if (e.key === 'ArrowRight') {
            this._modeDecade(1);
          } else if (e.key === 'ArrowUp') {
            this._modeDecade(-3);
          } else if (e.key === 'ArrowDown') {
            this._modeDecade(3);
          }
          setTimeout(this.focusYearCell.bind(this));
        }

        if (e.key === 'Tab') {
          setTimeout(() => {
            const focused = this.shadowRoot?.activeElement as HTMLElement;
            const firstEl = this.shadowRoot?.querySelector('button');

            // check if focus the last element, then focus the first element
            if (!e.shiftKey && !focused) firstEl?.focus();
            // check if focused element is inside current date-picker
            if (e.shiftKey && !this.shadowRoot?.contains(focused))
              this.focusDateCell();
          });
        }

        if (e.key === 'Escape') {
          this.onEscape?.(toDate(this.value));
        }
      },
      true
    );
  }

  /**
   * Focus on date-cell
   */
  focusDateCell() {
    const lastEl = this.shadowRoot?.querySelector(
      'button.date-cell[tabindex="0"]'
    ) as HTMLElement;
    lastEl?.focus();
  }

  focusMonthCell() {
    const lastEl = this.shadowRoot?.querySelector(
      'button.month-cell[tabindex="0"]'
    ) as HTMLElement;
    lastEl?.focus();
  }

  focusYearCell() {
    const lastEl = this.shadowRoot?.querySelector(
      'button.year-cell[tabindex="0"]'
    ) as HTMLElement;
    lastEl?.focus();
  }

  /**
   * check if date-cell is focused
   * @returns
   */
  isDateCellFocused() {
    const focused = this.shadowRoot?.activeElement as HTMLElement;
    return focused?.classList.contains('date-cell');
  }

  isMonthCellFocused() {
    const focused = this.shadowRoot?.activeElement as HTMLElement;
    return focused?.classList.contains('month-cell');
  }

  isYearCellFocused() {
    const focused = this.shadowRoot?.activeElement as HTMLElement;
    return focused?.classList.contains('year-cell');
  }

  openMonthSelector() {
    this._monthCursor = this.month;
    this._monthPickYearCursor = this.year;
    this._mode = 'month';
  }

  openYearSelector() {
    this._yearCursor = clamp(this.year, this._minYear, this._maxYear);
    this._mode = 'year';
    this._getYearMatrix();
  }

  override render() {
    const classes = classMap({
      'date-picker': true,
      [`date-picker--mode-${this._mode}`]: true,
      popup: this.popup,
    });
    const wrapperStyle = styleMap({
      'min-height': `${this.minHeight}px`,
    });
    return html`<div style=${wrapperStyle} class="date-picker-height-wrapper">
      <div class=${classes} style=${styleMap(this._cardStyle)}>
        ${this._switchMode({
          date: this._dateContent(),
          month: this._monthContent(),
          year: this._yearContent(),
        })}
      </div>
    </div>`;
  }

  toggleMonthSelector() {
    if (this._mode === 'month') this.closeMonthSelector();
    else this.openMonthSelector();
  }

  toggleYearSelector() {
    if (this._mode === 'year') this.closeYearSelector();
    else this.openYearSelector();
  }

  /**
   * Track the hovered date while a range selection is pending so the matrix
   * can preview the would-be range. No-op outside pending range selection.
   */
  private _onCellHover(date: Date | undefined) {
    const previewing = this.range && !!this._rangeStart && !this._rangeEnd;
    if (!previewing) {
      if (this._hoverDate) {
        this._hoverDate = undefined;
        this._getMatrix();
      }
      return;
    }
    if (date && this._hoverDate && isSameDay(this._hoverDate, date)) {
      return;
    }
    this._hoverDate = date;
    this._getMatrix();
  }

  override updated(_changedProperties: PropertyValues): void {
    if (_changedProperties.has('value')) {
      // Move the cursor to the provided date without touching any range
      // state: hosts seed `_rangeStart`/`_rangeEnd` directly when editing an
      // existing value, and that selection must survive the first update.
      if (this.value) this._cursor = toDate(this.value);
      this._getMatrix();
    }
  }

  /** date matrix */
  @property({ attribute: false })
  private accessor _matrix: DateCell[][] = [];

  @property({ attribute: false })
  private accessor _mode: 'date' | 'month' | 'year' = 'date';

  /** web-accessibility for month select */
  @property({ attribute: false })
  private accessor _monthCursor = 0;

  @property({ attribute: false })
  private accessor _monthPickYearCursor = 0;

  @property({ attribute: false })
  private accessor _yearCursor = 0;

  @property({ attribute: false })
  private accessor _yearMatrix: number[] = [];

  /** horizontal gap between cells in px */
  @property({ type: Number })
  accessor gapH = 10;

  /** vertical gap between cells in px */
  @property({ type: Number })
  accessor gapV = 8;

  @property({ attribute: false })
  accessor onChange: ((value: Date) => void) | undefined = undefined;

  @property({ attribute: false })
  accessor onClear: (() => void) | undefined = undefined;

  @property({ attribute: false })
  accessor onEscape: ((value: Date) => void) | undefined = undefined;

  /** card padding in px */
  @property({ type: Number })
  accessor padding = 20;

  @property({ type: Boolean })
  accessor popup: boolean = false;

  /** cell size in px */
  @property({ type: Number })
  accessor size = 28;

  /** Checked date timestamp */
  @property({ type: Number })
  accessor value: number | undefined = undefined;

  @property({ type: Boolean })
  accessor range = false;

  @property({ type: Boolean })
  accessor showTime = false;

  @property({ attribute: false })
  accessor onChangeRange:
    | ((value: { start: Date; end?: Date; hasTime?: boolean }) => void)
    | undefined = undefined;

  @property({ attribute: false })
  accessor _rangeStart: Date | undefined = undefined;

  @property({ attribute: false })
  accessor _rangeEnd: Date | undefined = undefined;

  /** hovered date used to preview a pending range selection */
  @property({ attribute: false })
  private accessor _hoverDate: Date | undefined = undefined;
}

declare global {
  interface HTMLElementTagNameMap {
    'date-picker': DatePicker;
  }
}
